import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface BannerAdData {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  link: string;
  buttonText: string;
  tier: 'gold' | 'silver' | 'bronze';
  businessName: string;
  backgroundColor: string;
  textColor: string;
  isActive: boolean;
  expiresAt: string;
}

interface BannerAdProps {
  className?: string;
  position?: 'top' | 'middle' | 'bottom';
  dismissible?: boolean;
  autoRotate?: boolean;
  rotationInterval?: number; // in milliseconds
}

const BannerAd: React.FC<BannerAdProps> = ({
  className = '',
  position = 'top',
  dismissible = true,
  autoRotate = true,
  rotationInterval = 10000
}) => {
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);

  // Mock banner ad data
  const mockBannerAds: BannerAdData[] = [
    {
      id: '1',
      title: 'Spring Queen Bee Pre-Orders Now Open!',
      description: 'Get the best Italian and Carniolan queens for the 2025 season. Limited availability - order now!',
      imageUrl: '/api/placeholder/120/80',
      link: '#',
      buttonText: 'Order Queens',
      tier: 'gold',
      businessName: 'Premium Queen Breeders',
      backgroundColor: 'bg-gradient-to-r from-yellow-400 to-orange-500',
      textColor: 'text-white',
      isActive: true,
      expiresAt: '2025-05-01'
    },
    {
      id: '2',
      title: 'Complete Beekeeping Starter Kits - 20% Off',
      description: 'Everything a new beekeeper needs! Hives, frames, tools, and protective gear in one package.',
      imageUrl: '/api/placeholder/120/80',
      link: '#',
      buttonText: 'Shop Now',
      tier: 'silver',
      businessName: 'Southern Bee Supply',
      backgroundColor: 'bg-gradient-to-r from-blue-500 to-purple-600',
      textColor: 'text-white',
      isActive: true,
      expiresAt: '2025-12-31'
    },
    {
      id: '3',
      title: 'Premium Wildflower Honey - Farm Fresh',
      description: 'Straight from our Georgia apiaries. Pure, raw, unfiltered honey with free shipping on orders over $50.',
      imageUrl: '/api/placeholder/120/80',
      link: '#',
      buttonText: 'Buy Honey',
      tier: 'bronze',
      businessName: 'Georgia Wildflower Honey Co.',
      backgroundColor: 'bg-gradient-to-r from-green-500 to-teal-600',
      textColor: 'text-white',
      isActive: true,
      expiresAt: '2025-09-30'
    }
  ];

  const activeBannerAds = mockBannerAds.filter(ad => ad.isActive);
  const currentAd = activeBannerAds[currentAdIndex];

  // Auto-rotate ads
  useEffect(() => {
    if (!autoRotate || activeBannerAds.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentAdIndex((prevIndex) => 
        (prevIndex + 1) % activeBannerAds.length
      );
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [autoRotate, activeBannerAds.length, rotationInterval]);

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  const handleAdClick = () => {
    // Track click event (in a real app, this would send analytics)
    console.log('Banner ad clicked:', currentAd.id);
    // Navigate to ad link
    window.open(currentAd.link, '_blank');
  };

  // Don't render if dismissed or no active ads
  if (isDismissed || !currentAd || activeBannerAds.length === 0) {
    return null;
  }

  const positionClasses = {
    top: 'mb-6',
    middle: 'my-8',
    bottom: 'mt-6'
  };

  return (
    <div className={`banner-ad ${positionClasses[position]} ${className}`}>
      <div className={`relative rounded-lg overflow-hidden shadow-lg ${currentAd.backgroundColor} ${currentAd.textColor}`}>
        {/* Dismissible X button */}
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 z-10 p-1 rounded-full bg-black bg-opacity-20 hover:bg-opacity-30 transition-all duration-200"
            aria-label="Dismiss ad"
          >
            <XMarkIcon className="h-5 w-5 text-white" />
          </button>
        )}

        {/* Ad Content */}
        <div className="flex items-center p-4 cursor-pointer" onClick={handleAdClick}>
          {/* Image */}
          <div className="flex-shrink-0 mr-4">
            <img
              src={currentAd.imageUrl}
              alt={currentAd.title}
              className="w-20 h-14 object-cover rounded-md border-2 border-white border-opacity-20"
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1 min-w-0 mr-4">
                <h3 className="text-lg font-bold truncate">
                  {currentAd.title}
                </h3>
                <p className="text-sm opacity-90 mt-1 line-clamp-2 sm:line-clamp-1">
                  {currentAd.description}
                </p>
                <p className="text-xs opacity-75 mt-1">
                  Sponsored by {currentAd.businessName}
                </p>
              </div>

              {/* Call to Action Button */}
              <div className="flex-shrink-0 mt-3 sm:mt-0">
                <button className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-medium rounded-md transition-all duration-200 backdrop-blur-sm border border-white border-opacity-30">
                  {currentAd.buttonText}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicators (for multiple ads) */}
        {activeBannerAds.length > 1 && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {activeBannerAds.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentAdIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  index === currentAdIndex 
                    ? 'bg-white' 
                    : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                }`}
                aria-label={`Go to ad ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Tier Badge */}
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
            currentAd.tier === 'gold' 
              ? 'bg-yellow-200 text-yellow-800'
              : currentAd.tier === 'silver'
              ? 'bg-gray-200 text-gray-800' 
              : 'bg-orange-200 text-orange-800'
          }`}>
            {currentAd.tier}
          </span>
        </div>
      </div>

      {/* Additional Info Bar */}
      <div className="flex justify-between items-center text-xs text-gray-500 mt-2 px-1">
        <span>Advertisement</span>
        {activeBannerAds.length > 1 && (
          <span>
            {currentAdIndex + 1} of {activeBannerAds.length}
          </span>
        )}
      </div>
    </div>
  );
};

export default BannerAd;
